import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import FormFieldTokenNumber from '../../../components/FormField/FormFieldTokenNumber';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import {
  createTokenRecoveryPassword,
  validateCodeFromEmail
} from '../../../services/tokenValidation';
import AlertBox, {
  AlertBoxHandles
} from '../../../components/AlertBox/AlertBox';
import { useAccountRecoveryContext } from '../../../data/AccountRecovery/AccountRecoveryContext';
import { getSocialsLoginByIdentifier } from '../../../services/queryController';
import { ErrorMessages } from '../../../data/ErrorMessages';
import { AttemptsTypeEnum } from '../../../enums/AttemptsTypeEnum';
import { SendCodeToEmailEventsEnum } from '../../../enums/SendCodeToEmailEventsEnum';
import StepHeader from '../../../components/Wizard/StepHeader';
import TimerButton from '../../../components/Button/TimerButton';
import { CODE_INCORRECT_TOKEN } from '../../../data/Constants';
import CodeNotArrive from '../../CodeNotArrive';

export interface CheckTokenProps {
  goBackView?: React.Dispatch<React.SetStateAction<boolean>>;
  callCreateToken(e: any): any;
  previousPath?: number;
  nextPath?: number | null;
  handleChangeStep(e: number): void;
  context: any;
}

export default function CheckToken({
  nextPath,
  handleChangeStep,
  previousPath,
  context
}: CheckTokenProps) {
  const {
    getOrigin,
    setControllerViews,
    showCooldownErrorMessage,
    showErrorMessageServer,
    controllerViews,
    attempts,
    increaseAttemptByType
  } = useControllerFlowStepsContext();
  const { getValues, setValue } = useAccountRecoveryContext();
  const [fieldValidate, setValueFieldValidate] = useState({
    value: '',
    isValid: false
  });
  const [tokenStatus, setTokenStatus] = useState<'none' | 'error' | 'success'>(
    'none'
  );
  const [resendingCode, setResendingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);

  const MAX_EMAIL_ATTEMPTS = 4;
  const currentAttempts =
    attempts?.[AttemptsTypeEnum.VALIDATE_EMAIL_RECOVERY_PASSWORD] ?? 0;

  const url = `${
    !getOrigin()
      ? '/recuperar-senha/token'
      : '/' + getOrigin() + '/recuperar-senha/token'
  }`;
  const navigate = useNavigate();

  useEffect(() => {
    navigate(url);
  }, [navigate]);

  useEffect(() => {
    if (fieldValidate.isValid && !tokenValidated && !loading) {
      validateToken();
    }
  }, [fieldValidate.isValid]);

  function backPage() {
    if (previousPath == 0 || previousPath) {
      handleChangeStep(previousPath);
    }
  }

  window.onpopstate = () => {
    backPage();
  };

  async function validateToken() {
    const valueKey = getValues('email');

    if (valueKey && fieldValidate.value) {
      setLoading(true);
      setTokenStatus('none');

      try {
        await validateCodeFromEmail(
          valueKey,
          fieldValidate.value,
          SendCodeToEmailEventsEnum.RESET_PASSWORD_EMAIL,
          ErrorMessages.CODE_EMAIL_FORGOT_PASSWORD
        );

        setValue('code', fieldValidate.value);
        setTokenStatus('success');
        setTokenValidated(true);
        setLoading(false);
      } catch (error: any) {
        if (error?.code === CODE_INCORRECT_TOKEN) {
          setTokenStatus('error');
        } else {
          showErrorMessageServer();
        }
        setTokenValidated(false);
        setResendingCode(false);
        setLoading(false);
      }
    }
  }

  async function proceedToNextStep() {
    if (!tokenValidated) return;

    setLoading(true);
    const valueKey = getValues('email');

    try {
      const responsePendingData = await getSocialsLoginByIdentifier(
        valueKey,
        fieldValidate.value,
        'email'
      );

      if (responsePendingData.hasPassword)
        setControllerViews({ ...controllerViews, hasPassword: true });
      else
        setControllerViews({
          ...controllerViews,
          socialData: responsePendingData,
          hasPassword: false
        });

      nextStep();
    } catch (error) {
      console.error(error);
      showErrorMessageServer();
      setLoading(false);
    }
  }

  async function resendCode() {
    setTokenStatus('none');
    setTokenValidated(false);
    setResendingCode(true);
    await postCreateToken();
  }

  async function postCreateToken() {
    const formatValue = getValues('email');
    setLoading(true);
    try {
      await createTokenRecoveryPassword(formatValue);
      openAlert();
      setLoading(false);
    } catch (error: any) {
      increaseAttemptByType(AttemptsTypeEnum.VALIDATE_EMAIL_RECOVERY_PASSWORD);

      if (error?.statusCode === 429) showCooldownErrorMessage();
      else showErrorMessageServer();
      setLoading(false);
    }
  }

  const alertBoxRef = useRef<AlertBoxHandles>(null);
  function openAlert() {
    alertBoxRef.current?.openAlert();
  }

  function getEmail() {
    const email = getValues('email');
    if (!email) return '';
    try {
      return email;
    } catch {
      return '';
    }
  }

  function nextStep() {
    if (nextPath) handleChangeStep(nextPath);
  }

  if (currentAttempts >= MAX_EMAIL_ATTEMPTS) {
    return <CodeNotArrive label="e-mail" context={context} />;
  }

  return (
    <>
      <div className="card__section">
        <div className="card__top">
          <StepHeader
            title="Acesse seu e-mail"
            description={[
              `Enviamos um código com 6 números para o e-mail "${getEmail()}".`,
              'Confirme que você tem acesso ao e-mail informando este código aqui.'
            ]}
          />

          <div>
            <FormFieldTokenNumber
              fieldValidate={fieldValidate}
              status={tokenStatus}
              setState={setValueFieldValidate}
              resendingCode={resendingCode}
            />

            <TimerButton
              name="forget-password"
              isLoading={loading}
              type={AttemptsTypeEnum.VALIDATE_EMAIL_RECOVERY_PASSWORD}
              handleResend={resendCode}
            />
          </div>
        </div>

        <div className="button-group">
          <Button
            full
            onClick={proceedToNextStep}
            loading={loading}
            disabled={!tokenValidated}
          >
            <ButtonLabel loading={loading}>continuar</ButtonLabel>
          </Button>

          <Button customColors="btn-ghost" full onClick={backPage}>
            <ButtonLabel>voltar</ButtonLabel>
          </Button>
        </div>
      </div>
      <AlertBox ref={alertBoxRef} text="código enviado com sucesso" />
    </>
  );
}
